import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { addedMessage, afterStatus, applyBackground, loadStore as fetchStore, openDecision, readCloud, readStore, writeCloud } from "@hachi/api";
import type { AccountStatus, CloudCache, GateView, Session, StoreConfig } from "@hachi/api";
import { LEGACY_KEYS, PLAN_NAME } from "@hachi/core";
import { dailyPoint, garage, keepDataSafe, startSync, stopSync, sync, whenRejected } from "../data/garage";
import { trackAction } from "./track";
import { accountCall, auth, backendReady, clearFromCheckout, clearWantTrial, fromCheckout, store, syncSignedInCookie } from "./backend";

/** An answer for a form on a gate screen: an error to show, or null when it moved on. */
type FormResult = string | null;

interface AccountState {
  view: GateView;
  session: Session | null;
  cloud: CloudCache | null;
  storeConfig: StoreConfig;
  toast: ToastMessage | null;
  dismissToast: () => void;
  /** `soft` messages (nice to know, not needed) never push aside a message that has an action, like Undo. */
  showToast: (message: string, action?: ToastAction, soft?: boolean) => void;
  /** Ask the server again (gate "Try again", after sign-in). */
  check: (opts?: { fresh?: boolean; fromGate?: boolean }) => Promise<void>;
  /** After an email code or Google sign-in completed in this page. */
  signedIn: () => Promise<void>;
  /** Leave the gate without telling the server (a different account). */
  signOut: (notice?: string) => Promise<void>;
  /** Settings → sign out of this device. */
  signOutHere: () => Promise<void>;
  /** Settings → sign out another device. Returns false when it couldn't be done. */
  signOutOther: (id: string) => Promise<boolean>;
  /** A license key, on a gate screen or (inApp) on the plan page and upgrade sheet. */
  claim: (key: string, inApp?: boolean) => Promise<FormResult>;
  /** Gate: move the account here from a registered device. */
  move: (id: string) => Promise<FormResult>;
  /** Gate: quietly look for a purchase; moves on only when something changed. */
  quietCheck: () => Promise<void>;
  /** A buy link opened: look for the purchase when the person comes back. */
  watchPurchase: () => void;
}

export interface ToastAction { label: string; run: () => void }
export interface ToastMessage { id: number; message: string; action?: ToastAction }

const Ctx = createContext<AccountState | null>(null);

export function useAccount(): AccountState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAccount outside AccountProvider");
  return v;
}

const errorKey = (r: { reason?: string }) => (r.reason === "network" ? "network" : "server");
const TOAST_MS = 4500;
const TOAST_ACTION_MS = 7000;
let toastId = 0;
const PLAN_CHECK_MS = 30_000;
const WATCH_MS = 4000;
const WATCH_TRIES = 30;

/** Says what changed on the plan, once (`handleStatus`). */
function planChangeMessage(prev: CloudCache | null, next: CloudCache): string | null {
  const a = prev?.ent, b = next.ent;
  if (!a || !b || JSON.stringify(a) === JSON.stringify(b)) return null;
  if (a.trial && b.plan === "free") return "Your free trial has ended. Everything you saved is still here.";
  if (a.plan !== b.plan) return b.plan === "free" ? "You're on the free plan now. Everything you saved is still here." : `You're on ${PLAN_NAME[b.plan]} now`;
  if (a.cars !== b.cars || a.devices !== b.devices) {
    return `Your plan is updated: ${b.cars == null ? "unlimited cars" : `${b.cars} cars`}, ${b.devices} ${b.devices === 1 ? "device" : "devices"}`;
  }
  return null;
}

/**
 * Decides whether the app or a sign-in gate shows, like the current app's `cloudGate`:
 * an approved device opens straight away (offline too) and re-checks in the background.
 */
export function AccountProvider({ children }: { children: ReactNode }) {
  const [view, setView] = useState<GateView>(backendReady ? { view: "checking" } : { view: "app" });
  const [session, setSession] = useState<Session | null>(() => (backendReady ? auth.session() : null));
  const [cloud, setCloud] = useState<CloudCache | null>(() => (backendReady ? readCloud(store) : null));
  const [storeConfig, setStoreConfig] = useState<StoreConfig>(() => readStore(store));
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const started = useRef(false);
  const lastPlanCheck = useRef(0);
  const viewRef = useRef(view);
  viewRef.current = view;

  const showToast = useCallback((message: string, action?: ToastAction, soft?: boolean) => {
    setToast((cur) => (soft && cur?.action ? cur : { id: ++toastId, message, ...(action ? { action } : {}) }));
  }, []);
  const dismissToast = useCallback(() => setToast(null), []);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), toast.action ? TOAST_ACTION_MS : TOAST_MS);
    return () => clearTimeout(t);
  }, [toast]);

  const apply = useCallback(async (r: AccountStatus, fromGate: boolean) => {
    // Already at sign-in: a slower check coming back with no session mustn't replace the reason shown there.
    if (r.state === "signed-out" && viewRef.current.view === "sign-in") return;
    const before = readCloud(store);
    const step = afterStatus(r, auth.session(), before, Date.now());
    if (step.signOut) await auth.signOut();
    if (step.cloud !== undefined) { writeCloud(store, step.cloud); setCloud(step.cloud); }
    setSession(auth.session());
    // Coming through a gate: get the garage before opening the app (it opens anyway if that fails).
    if (fromGate && r.state === "ok") {
      setView({ view: "checking", message: "Getting your garage…" });
      await sync.now();
    }
    // A background re-check never interrupts work on an approved device with a server hiccup.
    if (fromGate || step.next.view !== "app") setView(step.next);
    if (step.next.view === "sign-in") stopSync();
    if (r.state === "ok") clearWantTrial();
    const say = addedMessage(r) ?? (!fromGate && step.cloud ? planChangeMessage(before, step.cloud) : null);
    if (say) showToast(say);
    // More room on the plan: what the limit held back goes up now.
    if (r.state === "ok" && !fromGate && JSON.stringify(before?.ent ?? null) !== JSON.stringify(r.ent ?? null)) void sync.now();
  }, [showToast]);

  const check = useCallback(async ({ fresh = false, fromGate = true } = {}) => {
    if (fromGate) setView({ view: "checking" });
    lastPlanCheck.current = Date.now();
    let r: AccountStatus;
    try { r = await accountCall("status", fresh ? { fresh: true } : {}); }
    catch { r = { state: "error", reason: "network" }; }
    await apply(r, fromGate);
  }, [apply]);

  const signedIn = useCallback(async () => {
    trackAction("signin");
    setSession(auth.session());
    await check({ fresh: true });
  }, [check]);

  const signOut = useCallback(async (notice?: string, manual = false) => {
    trackAction(manual ? "signout" : "signout-auto");
    await auth.signOut();
    writeCloud(store, null);
    setCloud(null);
    setSession(null);
    setView(notice ? { view: "sign-in", notice } : { view: "sign-in" });
  }, []);

  const signOutHere = useCallback(async () => {
    if (navigator.onLine) await sync.now();
    stopSync();
    garage.clear();
    // This browser stays the registered device, so signing back in here just works (D23).
    await signOut("You're signed out. Your data is safe in your account.", true);
  }, [signOut]);

  const signOutOther = useCallback(async (id: string) => {
    let r: AccountStatus;
    try { r = await accountCall("signout-device", { target: id }); }
    catch { return false; }
    if (r.state === "error") return false;
    await apply(r, false);
    return true;
  }, [apply]);

  const claim = useCallback(async (key: string, inApp = false): Promise<FormResult> => {
    let r: AccountStatus;
    try { r = await accountCall("claim", { key, ...(inApp ? { inApp: true } : {}) }); }
    catch { return "network"; }
    if (r.state === "error") return errorKey(r);
    if (r.state === "need-licence" || r.state === "key-error") return r.error ?? "server";
    await apply(r, !inApp);
    return null;
  }, [apply]);

  const move = useCallback(async (id: string): Promise<FormResult> => {
    let r: AccountStatus;
    try { r = await accountCall("move", { target: id }); }
    catch { return "network"; }
    if (r.state === "error") return errorKey(r);
    await apply(r, true);
    return null;
  }, [apply]);

  const quietCheck = useCallback(async () => {
    let r: AccountStatus;
    // This device may have been signed out earlier and signed in again: asking as "fresh" keeps it on the device screen.
    try { r = await accountCall("status", viewRef.current.view === "limit" ? { fresh: true } : {}); }
    catch { return; }
    // Nothing changed yet: leave the screen as it is, so nothing typed or being read is disturbed.
    if (r.state !== "need-licence" && r.state !== "limit" && r.state !== "error") await apply(r, true);
  }, [apply]);

  // After a purchase, Gumroad tells the server within seconds: keep asking until it shows up (`afterCheckout`).
  const watching = useRef<ReturnType<typeof setInterval> | null>(null);
  const afterCheckout = useCallback(() => {
    const before = JSON.stringify(readCloud(store)?.ent ?? null);
    let n = 0;
    const stop = () => { if (watching.current) clearInterval(watching.current); watching.current = null; };
    stop();
    const tick = async () => {
      n++;
      if (!auth.session() || n > WATCH_TRIES) { stop(); return; }
      if (viewRef.current.view !== "app") { await quietCheck(); return; }
      await check({ fromGate: false });
      if (JSON.stringify(readCloud(store)?.ent ?? null) !== before) stop();
    };
    void tick();
    watching.current = setInterval(() => void tick(), WATCH_MS);
  }, [check, quietCheck]);

  const watchPurchase = useCallback(() => {
    const back = () => {
      if (document.visibilityState !== "visible") return;
      document.removeEventListener("visibilitychange", back);
      afterCheckout();
    };
    setTimeout(() => document.addEventListener("visibilitychange", back), 300);
  }, [afterCheckout]);

  useEffect(() => {
    if (!backendReady || started.current) return;
    started.current = true;
    if (navigator.onLine) void fetchStore(store).then((c) => { setStoreConfig(c); applyBackground(c.theme?.bg); });
    keepDataSafe();
    whenRejected(() => { if (auth.session()) void check({ fromGate: false }); });
    void (async () => {
      if ((await garage.load()).source !== "new") dailyPoint();
      const back = await auth.finishGoogle(location.search);
      if (back) history.replaceState(null, "", location.pathname + back.back);
      if (back && "error" in back) { setView({ view: "sign-in", notice: back.error }); return; }
      if (back) trackAction("signin"); // coming back from Google
      const s = auth.session();
      setSession(s);
      const d = openDecision(s, readCloud(store), !!back, Date.now());
      setView(d.next);
      if (d.check) await check({ fresh: !!back });
      else if (navigator.onLine && (d.recheck || readCloud(store)?.ent?.trial)) await check({ fromGate: false });
    })();
  }, [check]);

  // Plans change elsewhere (admin, another device, a purchase): pick it up on coming back, and when a trial runs out.
  useEffect(() => {
    if (!backendReady) return;
    const refresh = () => {
      if (!navigator.onLine || !auth.session() || !readCloud(store) || Date.now() - lastPlanCheck.current < PLAN_CHECK_MS) return;
      void check({ fromGate: false });
    };
    const onVisible = () => { if (document.visibilityState === "visible" && viewRef.current.view === "app") refresh(); };
    const onTimer = () => {
      const t = readCloud(store)?.ent?.trial;
      if (viewRef.current.view === "app" && t && Date.parse(t.endsAt) <= Date.now()) refresh();
    };
    const onHash = () => { if (location.hash && location.hash !== "#/") store.setItem(LEGACY_KEYS.lastView, location.hash); };
    document.addEventListener("visibilitychange", onVisible);
    addEventListener("hashchange", onHash);
    const timer = setInterval(onTimer, 60_000);
    return () => { document.removeEventListener("visibilitychange", onVisible); removeEventListener("hashchange", onHash); clearInterval(timer); };
  }, [check]);

  // Back from checkout on the website: look for the purchase as soon as the app is open.
  useEffect(() => {
    if (view.view === "app" && backendReady && fromCheckout()) { clearFromCheckout(); afterCheckout(); }
  }, [view, afterCheckout]);

  // Sync runs while the app is open.
  useEffect(() => { if (view.view === "app" && backendReady) { startSync(); void sync.now(); } }, [view.view]);

  // Keep the website's "signed in" flag current.
  useEffect(() => { if (backendReady) syncSignedInCookie(!!session); }, [session]);

  const value = useMemo(() => ({
    view, session, cloud, storeConfig, toast, dismissToast, showToast, check, signedIn, signOut, signOutHere, signOutOther, claim, move, quietCheck, watchPurchase,
  }), [view, session, cloud, storeConfig, toast, dismissToast, showToast, check, signedIn, signOut, signOutHere, signOutOther, claim, move, quietCheck, watchPurchase]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
